/**
 * 员工登录页：工号 + 口令 +（启用二次验证时）6 位 TOTP 验证码。
 *
 * 三种状态必须能被区分（服务端口径见 apps/api/src/modules/admin.routes.ts）：
 *   · 口令错误         → 401「账号或口令不正确」（与账号不存在同一文案，防枚举）
 *   · 需要二次验证码   → **200** `{ needTotp: true }`，这不是失败，界面切到验证码输入
 *   · 账号锁定         → 403，文案里带剩余分钟数
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, isNeedTotp, staffLogin } from '../lib/api.ts';
import { formatDate, parseLockMinutes } from '../lib/format.ts';

type Stage = 'credentials' | 'otp';

interface LockInfo {
  minutes: number | null;
}

export function LoginPage({ onLoggedIn }: { onLoggedIn: (token: string) => Promise<void> }) {
  const [stage, setStage] = useState<Stage>('credentials');
  const [no, setNo] = useState('S0001');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState('');
  const [locked, setLocked] = useState<LockInfo | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setLocked(null);
    try {
      const result = await staffLogin({
        no: no.trim(),
        password,
        totp: stage === 'otp' && totp.length === 6 ? totp : undefined,
      });

      if (isNeedTotp(result)) {
        // 口令正确但缺验证码：切到验证码输入，不当失败处理
        setStage('otp');
        setBusy(false);
        return;
      }

      await onLoggedIn(result.token);
    } catch (err) {
      if (err instanceof ApiError) {
        // 403 = 账号锁定（文案里带剩余分钟），如实展示剩余时间
        if (err.status === 403) {
          setLocked({ minutes: parseLockMinutes(err.message) });
          setStage('credentials');
        } else {
          setLocked(null);
          // 验证码错误时留在验证码输入步骤，避免用户以为要重新输口令
          setStage(err.status === 401 && stage === 'otp' && /验证码/.test(err.message) ? 'otp' : 'credentials');
        }
        setError(err.message);
      } else {
        setError('登录请求失败，请检查后端服务是否已启动');
      }
      setBusy(false);
    }
  };

  const resetToCredentials = (): void => {
    setStage('credentials');
    setTotp('');
    setError('');
    setLocked(null);
  };

  return (
    <div className="login-page">
      <div className="login-intro">
        <div className="brand-mark">HEW³</div>
        <div className="li-title">中台与后台控制台</div>
        <div className="li-desc">
          三螺旋企业家生态健康守护系统 · 员工工作台。
          <br />
          角色决定「能不能做」，守护关系决定「能对谁做」，两者同时成立才放行。
        </div>
        <div className="note-src">
          <b>双因子认证</b>
          <br />
          后台可接触成员的敏感健康与财富信息，因此员工账号采用「工号 + 口令 + TOTP 验证码」。
          员工凭证与成员凭证受众隔离（<span className="mono-num">aud=staff</span>），互不通用。
        </div>
        <div className="note-src" style={{ marginTop: 10 }}>
          <b>演示账号</b>
          <br />
          工号 <span className="mono-num">S0001</span>（超级管理员，已启用 TOTP）；
          <span className="mono-num"> S0003</span>–<span className="mono-num">S0006</span> 未启用 TOTP。
          口令与验证码密钥见 <span className="mono-num">docs/权限模型.md</span> 演示账号一节。
        </div>
      </div>

      <form className="login-card" onSubmit={(e) => void submit(e)}>
        <div className="lc-title">员工登录</div>
        <div className="lc-sub">STAFF SIGN IN · 仅限内部员工使用</div>
        <div className="sp16" />

        {locked && (
          <div className="alert-chip danger">
            <span>!</span>
            <span>
              账号已锁定
              {locked.minutes !== null ? `，剩余 ${locked.minutes} 分钟` : ''}。
              {locked.minutes !== null ? '锁定期间即使口令正确也无法登录。' : ''}
            </span>
          </div>
        )}

        {!locked && stage === 'otp' && (
          <div className="alert-chip info">
            <span>◍</span>
            <span>该账号已启用二次验证，请输入认证器中的 6 位验证码（30 秒刷新一次）。</span>
          </div>
        )}

        <div className="field">
          <label className="field-label" htmlFor="staff-no">
            工号
          </label>
          <input
            id="staff-no"
            className="field-input"
            autoComplete="username"
            placeholder="例如 S0001"
            value={no}
            disabled={stage === 'otp'}
            onChange={(e) => setNo(e.target.value.toUpperCase())}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="staff-password">
            口令
          </label>
          <input
            id="staff-password"
            className="field-input"
            type="password"
            autoComplete="current-password"
            placeholder="请输入登录口令"
            value={password}
            disabled={stage === 'otp'}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {stage === 'otp' && (
          <div className="field">
            <label className="field-label" htmlFor="staff-totp">
              6 位验证码
            </label>
            <input
              id="staff-totp"
              className="field-input otp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="······"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <div className="field-hint">认证器 App 每 30 秒生成一个新的验证码。</div>
          </div>
        )}

        {error && <div className="field-error">{error}</div>}

        <div className="sp12" />
        <button
          type="submit"
          className="btn primary block"
          disabled={busy || no.trim().length < 2 || password.length < 6 || (stage === 'otp' && totp.length !== 6)}
        >
          {busy ? '正在验证…' : stage === 'otp' ? '提交验证码并登录' : '登录'}
        </button>

        {stage === 'otp' && (
          <>
            <div className="sp8" />
            <button type="button" className="btn sm block" onClick={resetToCredentials} disabled={busy}>
              返回修改工号或口令
            </button>
          </>
        )}

        <div className="field-hint">
          连续 5 次口令失败将锁定账号 15 分钟；同一 IP 每 5 分钟最多 15 次登录尝试。
        </div>
        <div className="field-hint">上次会话有效期与退出记录由服务端会话表管理，退出登录即吊销。</div>
        <div className="field-hint muted">当前演示日期口径：{formatDate(new Date().toISOString())}</div>
      </form>
    </div>
  );
}
